import { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type {
  ImportSourceConfig,
  ShopifyImportSource,
  GenericUrlImportSource,
  SquareOnlineImportSource,
  WooCommerceImportSource,
} from '../../types';
import {
  extractProductDataWithBackoff,
  extractProductUrls,
  getParentChildReferenceCountForBrand,
  deleteAllReferencesForBrand,
  fetchHtmlServerSide,
  fetchStoreApiText,
  ExtractionCancelledError,
  NeedsManualHtmlError,
  UNREACHABLE_LISTING_MESSAGE,
  type ExtractedProduct,
  type ExtractedProductUrls,
} from '../../services/firebase';
import { dedupeProductUrls } from '../../utils/productUrls';
import {
  excludedKeySet,
  isUrlExcluded,
  toggleUrlExclusion,
} from '../../utils/importExclusions';
import { fetchShopifyProducts, parseProducts } from '../CuratedWatches/ShopifyScraper';
import { getReferencesByDocIds } from '../../services/firebase';
import { docIdsToCheck, testStructureLines, type TestStructureInput } from './testStructure';
import { loadSquareSource } from '../CuratedWatches/SquareOnlineScraper';
import { loadWooSource } from '../CuratedWatches/WooCommerceScraper';
import { storeScopeSummary, storeVariantSummary } from '../CuratedWatches/storeScope';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { PasteHtmlModal } from '../common/PasteHtmlModal';
import { ShopifySourceEditor } from './ShopifySourceEditor';
import { GenericSourceEditor } from './GenericSourceEditor';
import { JsonFeedSourceEditor } from './JsonFeedSourceEditor';
import { ManualSourceEditor } from './ManualSourceEditor';
import { SquareOnlineSourceEditor } from './SquareOnlineSourceEditor';
import { WooCommerceSourceEditor } from './WooCommerceSourceEditor';

interface SourceCardProps {
  source: ImportSourceConfig;
  brandId: string;
  /**
   * The brand's `mainWebsite`, offered as a one-click fill for an empty
   * Square Online / WooCommerce URL (new sources are prefilled on creation;
   * this covers ones created blank).
   */
  brandWebsite?: string;
  /** When true, the "Test" preview extraction bypasses the Firestore cache. */
  skipCache?: boolean;
  onChange: (updated: ImportSourceConfig) => void;
  onRemove: () => void;
  /**
   * `prefetched` carries every product this card's Test preview already
   * extracted — including pages the user pasted by hand — so the import can
   * reuse them instead of re-fetching a site that blocked us the first time.
   */
  onImportNow?: (
    source: ImportSourceConfig,
    prefetched: Array<{ url: string; extracted: ExtractedProduct }>,
  ) => void;
  /**
   * Square Online / WooCommerce: open the manual "paste the JSON" dialog — for
   * a shop that refuses our automated requests (Import Now opens it by itself
   * when that happens; this is the way in by hand).
   */
  onPasteJson?: (source: SquareOnlineImportSource | WooCommerceImportSource) => void;
}

const KIND_LABELS: Record<ImportSourceConfig['kind'], string> = {
  shopify: 'Shopify',
  generic: 'Generic URL',
  'json-feed': 'Extractor JSON',
  manual: 'Manual',
  'square-online': 'Square Online',
  woocommerce: 'WooCommerce',
};

type TestItemStatus = 'pending' | 'loading' | 'success' | 'failure';

type TestItem =
  | {
      type: 'collection';
      url: string;
      status: TestItemStatus;
      result?: ExtractedProductUrls;
      error?: string;
      /** Live status text shown while loading (e.g. retry progress). */
      note?: string;
    }
  | {
      type: 'direct';
      url: string;
      status: TestItemStatus;
      result?: ExtractedProduct;
      error?: string;
      /** Live status text shown while loading (e.g. retry progress). */
      note?: string;
    };

export function SourceCard({
  source,
  brandId,
  brandWebsite,
  skipCache = false,
  onChange,
  onRemove,
  onImportNow,
  onPasteJson,
}: SourceCardProps) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testItems, setTestItems] = useState<TestItem[] | null>(null);
  // URLs that hit NeedsManualHtmlError (or were force-queued) and await a manual
  // HTML paste. Non-empty → the shared <PasteHtmlModal> walks the user through them.
  const [pasteQueue, setPasteQueue] = useState<string[]>([]);
  // When ticked, skip auto-extraction entirely and send every product URL straight
  // to the paste queue — mirrors the Bulk Import wizard's "Force manual HTML paste".
  const [forceManualPaste, setForceManualPaste] = useState(false);
  const cancelRef = useRef(false);

  // ── Per-URL exclusions ──────────────────────────────────────────────────────
  // Held on the source config (not in testItems, which is rebuilt from scratch
  // on every Test run), so a page you've ticked off stays excluded across runs,
  // reloads and re-tests.
  const excludedUrls = source.kind === 'generic' ? source.excludedUrls : undefined;
  const excludedKeys = useMemo(() => excludedKeySet(excludedUrls), [excludedUrls]);
  // Mirrored into a ref so the in-flight test loop sees a tick immediately:
  // testGeneric re-reads this before each URL, so excluding a row part-way
  // through a run skips every URL it hasn't reached yet (same pattern as
  // cancelRef). Rows already extracted flip to dimmed and are dropped from the
  // Import Now handoff, so nothing excluded is ever imported either way.
  const excludedKeysRef = useRef(excludedKeys);
  excludedKeysRef.current = excludedKeys;

  const imageSelector =
    source.kind === 'generic' ? source.cssSelectorOverrides?.image?.trim() || undefined : undefined;

  const toggleExclude = (url: string) => {
    if (source.kind !== 'generic') return;
    const next = toggleUrlExclusion(source.excludedUrls, url);
    onChange({ ...source, excludedUrls: next.length > 0 ? next : undefined });
  };

  const clearExclusions = () => {
    if (source.kind !== 'generic') return;
    onChange({ ...source, excludedUrls: undefined });
  };

  // Toggling `ignoreParentChildRelationships` mid-lifecycle would leave the
  // brand's existing parent/variant refs inconsistent with the new import
  // intent. When the flag flips AND the brand already holds parent-child refs,
  // hold the change behind a confirmation modal (`pcPrompt`); a fresh source
  // (no such refs) saves straight through. See handleShopifyChange.
  const [pcChecking, setPcChecking] = useState(false);
  const [pcPrompt, setPcPrompt] = useState<
    { next: ShopifyImportSource; count: number } | null
  >(null);
  const [pcClearing, setPcClearing] = useState(false);

  // Intercept Shopify-source edits: only the parent-child flag flipping is
  // gated — every other field edit passes straight through to onChange.
  const handleShopifyChange = (updated: ShopifyImportSource) => {
    const prev = source as ShopifyImportSource;
    const before = prev.ignoreParentChildRelationships ?? false;
    const after = updated.ignoreParentChildRelationships ?? false;
    if (before === after) {
      onChange(updated);
      return;
    }
    // The flag toggled — check for existing parent-child refs before applying.
    setPcChecking(true);
    getParentChildReferenceCountForBrand(brandId)
      .then((count) => {
        if (count > 0) {
          setPcPrompt({ next: updated, count });
        } else {
          onChange(updated); // fresh source — nothing to leave inconsistent
        }
      })
      .catch((err) => {
        // Don't silently drop the toggle: surface the failure and leave the
        // checkbox reverted (onChange not called) so the user can retry.
        console.error('Failed to check parent-child refs:', err);
        toast.error('Could not check existing references — toggle not saved.');
      })
      .finally(() => setPcChecking(false));
  };

  // Modal action — save the flag change, leaving existing refs untouched.
  const applyPromptKeepRefs = () => {
    if (!pcPrompt) return;
    onChange(pcPrompt.next);
    setPcPrompt(null);
  };

  // Modal action — save the flag change AND delete every ref for this brand so
  // the next import rebuilds cleanly under the new parent-child intent.
  const applyPromptClearRefs = async () => {
    if (!pcPrompt) return;
    setPcClearing(true);
    try {
      const deleted = await deleteAllReferencesForBrand(brandId);
      onChange(pcPrompt.next);
      toast.success(
        `Deleted ${deleted} reference${deleted === 1 ? '' : 's'}. Re-import to rebuild.`,
      );
      setPcPrompt(null);
    } catch (err) {
      console.error('Failed to clear brand references:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to clear references');
    } finally {
      setPcClearing(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestItems(null);
    setPasteQueue([]);
    cancelRef.current = false;
    try {
      if (source.kind === 'shopify') {
        await testShopify(source, brandId, setTestResult);
      } else if (source.kind === 'square-online') {
        await testSquareOnline(source, brandId, setTestResult);
      } else if (source.kind === 'woocommerce') {
        await testWooCommerce(source, brandId, setTestResult);
      } else if (source.kind === 'generic') {
        const manualUrls = await testGeneric(
          source,
          setTestItems,
          cancelRef,
          skipCache,
          forceManualPaste,
          excludedKeysRef,
        );
        if (manualUrls.length > 0) {
          toast(
            `${manualUrls.length} URL${manualUrls.length === 1 ? '' : 's'} need manual HTML paste`,
            { icon: '📋' },
          );
          setPasteQueue(manualUrls);
        }
      } else {
        setTestResult('No automated test available for this source kind.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Test failed';
      setTestResult(`Error: ${msg}`);
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  // Mark every queued-but-unparsed URL as skipped when the paste modal is
  // dismissed without finishing it (still 'pending' → user skipped/closed).
  const markPendingSkipped = () => {
    setTestItems((prev) =>
      prev
        ? prev.map((it) =>
            it.type === 'direct' && it.status === 'pending' && pasteQueue.includes(it.url)
              ? { ...it, status: 'failure', error: 'Manual HTML paste skipped' }
              : it,
          )
        : null,
    );
  };

  // Stop the test run. `cancelRef` is checked between URLs AND polled inside
  // extractProductDataWithBackoff's sleep, so a stop during a 256s backoff wait
  // lands immediately instead of after the remaining retries.
  const handleStop = () => {
    cancelRef.current = true;
    toast('Stopping…', { icon: '🛑' });
  };

  // Everything the preview successfully extracted, keyed by URL — handed to the
  // Bulk Import wizard on "Import Now" so pasted/blocked pages don't get
  // re-fetched from scratch.
  // A URL excluded AFTER it was already extracted is still dropped here, so a
  // late tick can't smuggle a result through to the import.
  const prefetchedFromTest = (): Array<{ url: string; extracted: ExtractedProduct }> =>
    (testItems ?? []).flatMap((it) =>
      it.type === 'direct' &&
      it.status === 'success' &&
      it.result &&
      !isUrlExcluded(it.url, excludedKeys)
        ? [{ url: it.url, extracted: it.result }]
        : [],
    );

  // Re-run the full backoff sequence (2/4/…/256s, up to 8 retries) for a single
  // failed URL without disturbing the other rows. On success the row flips to ✓;
  // if it exhausts again it returns to 'failure' so the buttons come back.
  const retryItem = async (url: string) => {
    const method = source.kind === 'generic' ? source.extractionMethod ?? 'auto' : 'auto';
    const patch = (p: Partial<Extract<TestItem, { type: 'direct' }>>) =>
      setTestItems((prev) =>
        prev
          ? prev.map((it) =>
              it.type === 'direct' && it.url === url ? ({ ...it, ...p } as TestItem) : it,
            )
          : null,
      );
    // Clear any stop left over from the batch run, or this retry aborts instantly.
    cancelRef.current = false;
    patch({ status: 'loading', note: 'Trying…', error: undefined });
    try {
      const res = await extractProductDataWithBackoff(
        url,
        skipCache,
        method,
        ({ attempt, waitMs, phase }) =>
          patch({
            status: 'loading',
            note:
              attempt === 1
                ? 'Trying…'
                : phase === 'waiting'
                  ? `Retrying (attempt ${attempt}, ${waitMs / 1000}s wait)…`
                  : `Retry attempt ${attempt}, fetching…`,
          }),
        () => cancelRef.current,
        imageSelector,
      );
      patch({ status: 'success', result: res, note: undefined, error: undefined });
    } catch (err) {
      patch({
        status: 'failure',
        note: undefined,
        error:
          err instanceof ExtractionCancelledError
            ? 'Stopped — retry or enter HTML.'
            : err instanceof NeedsManualHtmlError
              ? 'Automated fetch failed — enter HTML manually.'
              : err instanceof Error
                ? err.message
                : String(err),
      });
    }
  };

  // Surface the existing manual-paste flow for one URL on demand (the same
  // <PasteHtmlModal> the batch uses — just triggered by a button, not auto-run).
  const enterHtml = (url: string) => setPasteQueue([url]);

  const canTest =
    source.kind === 'shopify' ||
    source.kind === 'generic' ||
    source.kind === 'square-online' ||
    source.kind === 'woocommerce';
  // 'json-feed' has nothing to Test (there's no URL to fetch — the operator
  // supplies the file), but it very much has something to import: Import Now
  // opens the wizard straight on the upload step.
  const canImportNow =
    onImportNow &&
    (source.kind === 'shopify' ||
      source.kind === 'generic' ||
      source.kind === 'json-feed' ||
      ((source.kind === 'square-online' || source.kind === 'woocommerce') &&
        Boolean(source.categoryUrl.trim())));

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
            {KIND_LABELS[source.kind]}
          </span>
          <input
            type="text"
            value={source.label ?? ''}
            onChange={(e) =>
              onChange({ ...source, label: e.target.value || undefined })
            }
            placeholder="Label (optional)"
            className="text-sm border-b border-transparent hover:border-gray-300 focus:border-brand-500 focus:outline-none px-1"
          />
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <input
              type="checkbox"
              checked={source.enabled}
              onChange={(e) => onChange({ ...source, enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>
        <div className="flex items-center gap-2">
          {source.kind === 'generic' && (
            <label
              className="text-xs text-gray-500 flex items-center gap-1 cursor-pointer"
              title="Skip auto-extraction and paste each page's HTML by hand — useful for sites that block automated access (return 0 products)."
            >
              <input
                type="checkbox"
                checked={forceManualPaste}
                onChange={(e) => setForceManualPaste(e.target.checked)}
              />
              Force manual HTML paste
            </label>
          )}
          {canTest && (
            <Button variant="ghost" size="sm" onClick={handleTest} disabled={testing}>
              {testing ? 'Testing…' : 'Test'}
            </Button>
          )}
          {testing && source.kind === 'generic' && (
            <Button variant="ghost" size="sm" onClick={handleStop}>
              Stop
            </Button>
          )}
          {onPasteJson &&
            (source.kind === 'woocommerce' || source.kind === 'square-online') &&
            source.categoryUrl.trim() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPasteJson(source)}
                disabled={!source.enabled}
                title="For a shop that blocks automated requests: open it in your browser and paste what it shows"
              >
                Paste JSON…
              </Button>
            )}
          {canImportNow && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onImportNow!(source, prefetchedFromTest())}
              disabled={!source.enabled}
            >
              Import Now
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </div>

      {source.kind === 'shopify' && (
        <>
          <ShopifySourceEditor source={source} onChange={handleShopifyChange} />
          {pcChecking && (
            <p className="text-[11px] text-gray-400">
              Checking existing references…
            </p>
          )}
        </>
      )}
      {source.kind === 'generic' && (
        <GenericSourceEditor source={source} onChange={onChange} />
      )}
      {source.kind === 'json-feed' && (
        <JsonFeedSourceEditor source={source} onChange={onChange} />
      )}
      {source.kind === 'manual' && (
        <ManualSourceEditor source={source} onChange={onChange} />
      )}
      {source.kind === 'square-online' && (
        <SquareOnlineSourceEditor source={source} onChange={onChange} brandWebsite={brandWebsite} />
      )}
      {source.kind === 'woocommerce' && (
        <WooCommerceSourceEditor source={source} onChange={onChange} brandWebsite={brandWebsite} />
      )}

      {testResult && (
        <div className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-2 whitespace-pre-wrap text-gray-700">
          {testResult}
        </div>
      )}

      {testItems && testItems.length > 0 && (
        <TestItemList
          items={testItems}
          excludedKeys={excludedKeys}
          canExclude={source.kind === 'generic'}
          onToggleExclude={toggleExclude}
          onClearExclusions={clearExclusions}
          onRetry={retryItem}
          onEnterHtml={enterHtml}
        />
      )}

      {source.lastRun && (
        <p className="text-xs text-gray-500">
          Last run: {source.lastRun.status} — imported{' '}
          {source.lastRun.importedCount}
          {source.lastRun.parentsImported !== undefined && source.lastRun.variantsImported !== undefined
            ? ` (${source.lastRun.parentsImported} parents, ${source.lastRun.variantsImported} variants)`
            : ''}
          , updated {source.lastRun.updatedCount}
          {source.lastRun.parentsUpdated !== undefined && source.lastRun.variantsUpdated !== undefined
            ? ` (${source.lastRun.parentsUpdated} parents, ${source.lastRun.variantsUpdated} variants)`
            : ''}
          {source.lastRun.errorMessage ? ` · ${source.lastRun.errorMessage}` : ''}
        </p>
      )}

      {/* Manual HTML paste fallback for URLs the extractor couldn't reach. */}
      {pasteQueue.length > 0 && (
        <PasteHtmlModal
          queue={pasteQueue}
          method={source.kind === 'generic' ? source.extractionMethod ?? 'auto' : 'auto'}
          imageSelector={imageSelector}
          onResult={({ url, extracted }) =>
            setTestItems((prev) =>
              prev
                ? prev.map((it) =>
                    it.type === 'direct' && it.url === url
                      ? { ...it, status: 'success', result: extracted, error: undefined }
                      : it,
                  )
                : null,
            )
          }
          onDone={() => {
            markPendingSkipped();
            setPasteQueue([]);
          }}
          onCancel={() => {
            markPendingSkipped();
            setPasteQueue([]);
          }}
        />
      )}

      {/* Confirmation before flipping the parent-child flag on a brand that
          already holds parent/variant refs (see handleShopifyChange). */}
      {pcPrompt && (
        <Modal
          isOpen
          onClose={pcClearing ? () => {} : () => setPcPrompt(null)}
          title="Change parent-child grouping?"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              This brand has{' '}
              <span className="font-semibold">{pcPrompt.count}</span> existing
              parent/variant reference{pcPrompt.count === 1 ? '' : 's'}. Toggling
              the “ignore parent-child relationships” flag will leave those refs
              inconsistent with your new import intent. What would you like to do?
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="secondary"
                onClick={applyPromptKeepRefs}
                disabled={pcClearing}
              >
                Save and keep existing refs as-is
              </Button>
              <Button
                variant="danger"
                onClick={applyPromptClearRefs}
                disabled={pcClearing}
              >
                {pcClearing
                  ? 'Clearing references…'
                  : 'Save and clear all refs for this brand'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPcPrompt(null)}
                disabled={pcClearing}
              >
                Cancel
              </Button>
            </div>
            <p className="text-[11px] text-gray-400">
              “Keep” saves the flag but leaves refs untouched (clean up
              manually). “Clear all refs” permanently deletes every reference for
              this brand so a re-import rebuilds cleanly.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * The Test's structure block (parents / variants / singles, create vs update,
 * the no-native-grouping hint). Reads the brand's docs for these ids only; a
 * failed read still reports the structure.
 */
async function structureBlock(
  brandId: string,
  input: Omit<TestStructureInput, 'existingDocIds'>,
): Promise<string> {
  let existingDocIds: Set<string> | null = null;
  try {
    existingDocIds = new Set((await getReferencesByDocIds(brandId, docIdsToCheck(input.entries))).keys());
  } catch (err) {
    console.warn('Test: could not check existing references', err);
  }
  const lines = testStructureLines({ ...input, existingDocIds });
  return lines.length > 0 ? `\n\n${lines.join('\n')}` : '';
}

async function testShopify(
  source: ShopifyImportSource,
  brandId: string,
  setResult: (msg: string) => void,
): Promise<void> {
  if (!source.storeUrl.trim()) {
    setResult('Store URL is empty.');
    return;
  }
  setResult('Fetching products…');
  const products = await fetchShopifyProducts(source.storeUrl.trim(), (msg) =>
    setResult(msg),
  );
  const typeCounts = new Map<string, number>();
  for (const p of products) {
    const t = p.product_type || '(none)';
    typeCounts.set(t, (typeCounts.get(t) || 0) + 1);
  }
  const excluded = new Set(source.excludedProductTypes.map((t) => t.toLowerCase()));
  const remaining = products.filter(
    (p) => !excluded.has((p.product_type || '').toLowerCase()),
  );
  const typeSummary = Array.from(typeCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([t, c]) => `  ${t}: ${c}`)
    .join('\n');
  const summary =
    `Fetched ${products.length} products. After exclusions: ${remaining.length}.\n` +
    `Top product types:\n${typeSummary}`;
  setResult(`${summary}\n\nChecking what an import would do…`);
  const flatten = source.ignoreParentChildRelationships ?? false;
  const entries = parseProducts(remaining, source.storeUrl.trim().replace(/\/+$/, '')).map((e) =>
    flatten ? { ...e, shopifyVariantCount: 1 } : e,
  );
  setResult(
    summary +
      (await structureBlock(brandId, { entries, rule: source.variantGroupingRule, nativeGroupingOff: flatten })),
  );
}

async function testSquareOnline(
  source: SquareOnlineImportSource,
  brandId: string,
  setResult: (msg: string) => void,
): Promise<void> {
  if (!source.categoryUrl.trim()) {
    setResult('Shop or category URL is empty.');
    return;
  }
  setResult('Reading the storefront…');
  const loaded = await loadSquareSource(source.categoryUrl, {
    fetchPageHtml: fetchHtmlServerSide,
    onPage: (page, total) => setResult(`Fetching products… page ${page} of ${total}`),
    expandVariants: source.expandVariants !== false,
  });
  const withImage = loaded.entries.filter((e) => e.imgSrc).length;
  const outOfStock = loaded.products.filter((p) => p.badges?.out_of_stock).length;
  const sample = loaded.entries
    .slice(0, 8)
    .map((e) => `  ${e.model}${e.reference !== e.model ? ` (${e.reference})` : ''}`)
    .join('\n');
  setResult(
    `Store: ${loaded.store.domain ?? loaded.store.siteId}\n` +
      `${storeScopeSummary(loaded.products.length, loaded.scope, loaded.category?.name)}` +
      (loaded.category ? ` (#${loaded.category.siteCategoryId})` : '') +
      ` — visible only; ${withImage} with images, ${outOfStock} out of stock` +
      storeVariantSummary(loaded) +
      `.\nFirst products:\n${sample}` +
      (await structureBlock(brandId, { entries: loaded.entries })),
  );
}

async function testWooCommerce(
  source: WooCommerceImportSource,
  brandId: string,
  setResult: (msg: string) => void,
): Promise<void> {
  if (!source.categoryUrl.trim()) {
    setResult('Shop or category URL is empty.');
    return;
  }
  setResult('Reading the shop…');
  const loaded = await loadWooSource(source.categoryUrl, {
    fetchText: fetchStoreApiText,
    onPage: (page) => setResult(`Fetching products… page ${page}`),
    expandVariants: source.expandVariants !== false,
  });
  const withImage = loaded.entries.filter((e) => e.imgSrc).length;
  const outOfStock = loaded.products.filter((p) => p.is_in_stock === false).length;
  const sample = loaded.entries
    .slice(0, 8)
    .map(
      (e) =>
        `  ${e.model}${e.variant ? ` — ${e.variant}` : ''}${e.reference !== e.model ? ` (${e.reference})` : ''}` +
        (e.storeOptionCount ? ` — ${e.storeOptionCount} options` : ''),
    )
    .join('\n');
  setResult(
    `Shop: ${loaded.host}${loaded.currency ? ` (${loaded.currency})` : ''}\n` +
      `${storeScopeSummary(loaded.products.length, loaded.scope, loaded.category?.name)}` +
      (loaded.category ? ` (#${loaded.category.id})` : '') +
      ` — ${withImage} with images, ${outOfStock} out of stock` +
      storeVariantSummary(loaded) +
      `.\nFirst products:\n${sample}` +
      (await structureBlock(brandId, { entries: loaded.entries, rule: source.variantGroupingRule })),
  );
}

async function testGeneric(
  source: GenericUrlImportSource,
  setItems: (items: TestItem[] | ((prev: TestItem[] | null) => TestItem[])) => void,
  cancelRef: React.MutableRefObject<boolean>,
  skipCache: boolean,
  forceManual: boolean,
  // Re-read before each crawl/extract so a tick lands on the rest of the run.
  excludedKeysRef: React.MutableRefObject<Set<string>>,
): Promise<string[]> {
  const jsonListingUrl = source.jsonListingUrl?.trim();
  const manualUrls = source.productUrls;
  // A JSON listing URL overrides the HTML collection crawl.
  const crawlTargets = jsonListingUrl ? [jsonListingUrl] : source.collectionPageUrls;

  if (crawlTargets.length === 0 && manualUrls.length === 0) {
    setItems([
      {
        type: 'direct',
        url: '(none configured)',
        status: 'failure',
        error: 'No JSON listing, collection pages, or direct product URLs configured.',
      },
    ]);
    return [];
  }

  const productLink = source.cssSelectorOverrides?.productLink?.trim();
  const overrides = productLink ? { productLink } : undefined;
  const imageSelector = source.cssSelectorOverrides?.image?.trim() || undefined;

  // Maintain a local working copy as the source of truth so index-based updates
  // survive the list growing when we move from the crawl phase into the
  // per-product preview phase. This mirrors how Bulk Import crawls + merges, but
  // it's preview-only: nothing is written back to the source's URL textbox.
  let working: TestItem[] = crawlTargets.map((url) => ({ type: 'collection', url, status: 'pending' }));
  setItems(working);
  const apply = (index: number, patch: Partial<TestItem>) => {
    working = working.map((it, i) => (i === index ? ({ ...it, ...patch } as TestItem) : it));
    setItems(working);
  };

  // Phase 1 — discover product URLs (from the JSON listing, or by crawling each
  // collection page).
  const crawled: string[] = [];
  for (let i = 0; i < crawlTargets.length; i++) {
    if (cancelRef.current) return [];
    // An excluded listing page isn't crawled at all — the row stays neutral and
    // renders dimmed, so a whole junk collection costs nothing.
    if (isUrlExcluded(crawlTargets[i], excludedKeysRef.current)) {
      apply(i, { status: 'pending', note: undefined, error: undefined });
      continue;
    }
    apply(i, { status: 'loading' });
    try {
      const res = jsonListingUrl
        ? await extractProductUrls(crawlTargets[i], overrides, crawlTargets[i])
        : await extractProductUrls(crawlTargets[i], overrides);
      crawled.push(...res.urls);
      apply(i, { status: 'success', result: res });
    } catch (err) {
      apply(i, {
        status: 'failure',
        error:
          err instanceof NeedsManualHtmlError
            ? UNREACHABLE_LISTING_MESSAGE
            : err instanceof Error
              ? err.message
              : String(err),
      });
    }
  }

  // Phase 2 — preview the products: freshly-crawled URLs + manual direct URLs,
  // deduped (crawled first). Canonical dedup so a product listed on two
  // overlapping pagination pages only previews once.
  const productUrls = dedupeProductUrls([...crawled, ...manualUrls]);

  const offset = working.length;
  working = [
    ...working,
    ...productUrls.map<TestItem>((url) => ({ type: 'direct', url, status: 'pending' })),
  ];
  setItems(working);

  // Phase 3 — extract details for each product URL so the preview shows the
  // actual product (image, name, reference, calibre, …). URLs that the
  // extractor can't reach (NeedsManualHtmlError) — or every URL when
  // forceManual is on — are parked as 'pending' and collected for the manual
  // HTML paste fallback, which the caller drives via <PasteHtmlModal>.
  const needsManualHtml: string[] = [];
  for (let j = 0; j < productUrls.length; j++) {
    if (cancelRef.current) return needsManualHtml;
    const index = offset + j;
    const url = productUrls[j];

    // Excluded → never fetched, never queued for manual paste. Checked from the
    // ref (not a captured value) so ticking a row mid-run applies to every URL
    // the loop hasn't reached yet.
    if (isUrlExcluded(url, excludedKeysRef.current)) {
      apply(index, { status: 'pending', note: undefined, error: undefined });
      continue;
    }

    if (forceManual) {
      apply(index, { status: 'pending' });
      needsManualHtml.push(url);
      continue;
    }

    apply(index, { status: 'loading', note: 'Trying…', error: undefined });
    try {
      // Retry with exponential backoff (2/4/8/16/32/64/128/256s) so rate-limited sites
      // get several chances before we fall back to manual HTML paste. Success on
      // this URL doesn't carry any backoff state into the next one.
      const res = await extractProductDataWithBackoff(
        url,
        skipCache,
        source.extractionMethod,
        ({ attempt, waitMs, phase }) =>
          apply(index, {
            status: 'loading',
            note:
              attempt === 1
                ? 'Trying…'
                : phase === 'waiting'
                  ? `Retrying (attempt ${attempt}, ${waitMs / 1000}s wait)…`
                  : `Retry attempt ${attempt}, fetching…`,
          }),
        () => cancelRef.current,
        imageSelector,
      );
      apply(index, { status: 'success', result: res, note: undefined });
    } catch (err) {
      if (err instanceof ExtractionCancelledError) {
        // Stopped mid-URL — leave the row neutral and end the batch. Remaining
        // rows stay 'pending' so the Retry / Enter HTML buttons still apply.
        apply(index, { status: 'pending', note: undefined, error: undefined });
        return needsManualHtml;
      }
      if (err instanceof NeedsManualHtmlError) {
        // Park for manual paste — leave it neutral (pending), not a red failure.
        apply(index, { status: 'pending', error: undefined, note: undefined });
        needsManualHtml.push(url);
      } else {
        apply(index, { status: 'failure', error: err instanceof Error ? err.message : String(err), note: undefined });
      }
    }
  }
  return needsManualHtml;
}

function TestItemList({
  items,
  excludedKeys,
  canExclude,
  onToggleExclude,
  onClearExclusions,
  onRetry,
  onEnterHtml,
}: {
  items: TestItem[];
  excludedKeys: Set<string>;
  /** Exclusions are a generic-source feature; other kinds render read-only. */
  canExclude: boolean;
  onToggleExclude: (url: string) => void;
  onClearExclusions: () => void;
  onRetry: (url: string) => void;
  onEnterHtml: (url: string) => void;
}) {
  const excludedCount = items.filter((it) => isUrlExcluded(it.url, excludedKeys)).length;
  return (
    <div className="space-y-1">
      <ul className="space-y-1 text-xs">
        {items.map((item, idx) => (
          <li key={`${item.type}-${idx}-${item.url}`}>
            <TestItemRow
              item={item}
              excluded={isUrlExcluded(item.url, excludedKeys)}
              canExclude={canExclude}
              onToggleExclude={onToggleExclude}
              onRetry={onRetry}
              onEnterHtml={onEnterHtml}
            />
          </li>
        ))}
      </ul>
      {canExclude && (
        <div className="flex items-center gap-2 text-[11px] text-gray-500 px-1">
          <span>
            {items.length} URL{items.length === 1 ? '' : 's'}
            {excludedCount > 0 && ` · ${excludedCount} excluded`}
            {/* A tick updates the config in memory; it only reaches Firestore on
                Save changes (or Import Now, which saves first). The page has no
                dirty flag to key off, so this stays a plain standing note rather
                than a state-dependent "(unsaved)" that would go stale. */}
            {excludedCount > 0 && ' — saved with the source'}
          </span>
          {excludedCount > 0 && (
            <Button variant="ghost" size="sm" onClick={onClearExclusions}>
              Clear exclusions
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function TestItemRow({
  item,
  excluded,
  canExclude,
  onToggleExclude,
  onRetry,
  onEnterHtml,
}: {
  item: TestItem;
  excluded: boolean;
  canExclude: boolean;
  onToggleExclude: (url: string) => void;
  onRetry: (url: string) => void;
  onEnterHtml: (url: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = item.status === 'success' && !excluded;
  const statusBadge = excluded ? renderExcludedBadge() : renderStatusBadge(item.status);
  const typeLabel = item.type === 'collection' ? 'Collection' : 'Product';

  return (
    <div className="border border-gray-200 rounded">
      <div
        className={`flex items-center gap-2 px-2 py-1.5 ${
          canExpand ? 'cursor-pointer hover:bg-gray-50' : ''
        }`}
        onClick={() => {
          if (canExpand) setExpanded((v) => !v);
        }}
      >
        {canExclude && (
          <input
            type="checkbox"
            checked={excluded}
            onChange={() => onToggleExclude(item.url)}
            onClick={(e) => e.stopPropagation()}
            title="Exclude this URL — it won't be crawled, extracted or imported"
            aria-label={`Exclude ${item.url}`}
            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-brand-600"
          />
        )}
        {statusBadge}
        <span className="text-[10px] uppercase tracking-wide text-gray-500 w-16">
          {typeLabel}
        </span>
        <span
          className={`flex-1 truncate ${
            excluded ? 'text-gray-400 line-through opacity-60' : 'text-gray-700'
          }`}
          title={item.url}
        >
          {item.url}
        </span>
        {excluded && (
          <span className="text-[10px] uppercase tracking-wide text-gray-400 shrink-0">
            Excluded
          </span>
        )}
        {!excluded && item.status === 'success' && item.type === 'collection' && item.result && (
          <span className="text-gray-500">
            {item.result.urls.length} URLs · {item.result.method}
          </span>
        )}
        {!excluded && item.status === 'success' && item.type === 'direct' && item.result && (
          <span className="text-gray-500">
            {item.result.fieldsFound} fields · {item.result.extractionMethod}
          </span>
        )}
        {!excluded && item.status === 'loading' && item.note && (
          <span className="text-blue-600 truncate max-w-[45%]" title={item.note}>
            {item.note}
          </span>
        )}
        {!excluded && item.status === 'failure' && (
          <span className="text-red-600 truncate max-w-[30%]" title={item.error}>
            {item.error}
          </span>
        )}
        {!excluded && item.type === 'direct' && item.status === 'failure' && (
          <div
            className="flex shrink-0 items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant="ghost" size="sm" onClick={() => onRetry(item.url)}>
              Retry
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onEnterHtml(item.url)}>
              Enter HTML
            </Button>
          </div>
        )}
      </div>
      {expanded && !excluded && item.status === 'success' && (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-2">
          {item.type === 'collection' && item.result ? (
            <CollectionDetails result={item.result} />
          ) : item.type === 'direct' && item.result ? (
            <ProductDetails result={item.result} />
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Neutral badge for an excluded row — deliberately not a red failure. */
function renderExcludedBadge() {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-400">
      ⊘
    </span>
  );
}

function renderStatusBadge(status: TestItemStatus) {
  const map: Record<TestItemStatus, { label: string; cls: string }> = {
    pending: { label: '•', cls: 'bg-gray-100 text-gray-500' },
    loading: { label: '…', cls: 'bg-blue-100 text-blue-700' },
    success: { label: '✓', cls: 'bg-green-100 text-green-700' },
    failure: { label: '✕', cls: 'bg-red-100 text-red-700' },
  };
  const { label, cls } = map[status];
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

function CollectionDetails({ result }: { result: ExtractedProductUrls }) {
  if (result.urls.length === 0) {
    return <p className="text-gray-500">No URLs discovered.</p>;
  }
  return (
    <ol className="list-decimal pl-5 space-y-0.5 text-gray-700 break-all">
      {result.urls.map((u) => (
        <li key={u}>
          <a
            href={u}
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:underline"
          >
            {u}
          </a>
        </li>
      ))}
    </ol>
  );
}

function ProductDetails({ result }: { result: ExtractedProduct }) {
  const fields: Array<[string, string | undefined]> = [
    ['Reference', result.reference],
    ['Model', result.model],
    ['Collection', result.collection],
    ['Name', result.name],
    ['Price', result.price ? `${result.price}${result.currency ? ' ' + result.currency : ''}` : undefined],
    ['Movement', result.movement],
    ['Movement type', result.movementType],
    ['Case size', result.caseSize],
    ['Case material', result.caseMaterial],
    ['Case shape', result.caseShape],
    ['Crystal', result.crystal],
    ['Crown type', result.crownType],
    ['Water res', result.waterRes],
    ['Dial color', result.dialColor],
    ['Dial finish', result.dialFinish],
    ['Strap material', result.strapMaterial],
    ['Power reserve', result.powerReserve],
    ['Functions', result.functions],
  ];
  const present = fields.filter(([, v]) => v && String(v).trim() !== '');
  return (
    <div className="space-y-2">
      {result.imgSrc && (
        <img
          src={result.imgSrc}
          alt={result.name ?? ''}
          className="h-24 w-24 object-contain border border-gray-200 rounded bg-white"
        />
      )}
      {present.length > 0 ? (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          {present.map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <dt className="text-gray-500 min-w-[6rem]">{label}</dt>
              <dd className="text-gray-800 break-words">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-gray-500">No fields extracted.</p>
      )}
      {result.description && (
        <details className="text-gray-700">
          <summary className="cursor-pointer text-gray-500">Description</summary>
          <p className="mt-1 whitespace-pre-wrap">{result.description}</p>
        </details>
      )}
    </div>
  );
}
